import React from 'react';
import once from 'lodash.once';
import PropTypes from 'prop-types';
import classNames from 'classnames'

import {MESO_AD_EVENTS, MESO_SLOT_STATUSES} from '../../constants'
import {Context} from '../MesoAdsContext/MesoAdsContext';
import {Observer} from '../Observer/Observer';
import {Placeholder} from '../Placeholder/Placeholder';
import {ErrorBoundary} from '../ErrorBoundary/ErrorBoundary'
import {
    emitAdView,
    emitAdFailed,
    emitAdSucceeded
} from './events';

import './MesoAds.less';

class MesoSlot extends React.Component {
    constructor(props) {
        super(props);
        this.onMesoAd = this.onMesoAd.bind(this);
        this.onError = this.onError.bind(this);
        this.onVisibleOnce = once(this.onVisible.bind(this));

        this.mesoDisplayAdRef = React.createRef();
        this.state = {
            status: MESO_SLOT_STATUSES.LOADING
        };
    }

    componentDidMount() {
        this.props.onAdLoading && this.props.onAdLoading();
        this.mesoDisplayAdRef.current.addEventListener("mesoAd", this.onMesoAd);
    }

    componentWillUnmount() {
        this.mesoDisplayAdRef.current.removeEventListener("mesoAd", this.onMesoAd);
    }

    onMesoAd(event) {
        const {props: {gaiaId, slot, onAdLoadSuccess, onAdLoadFailed}} = this;

        const {code, uci, src} = event.detail;

        const newState = {
            adUci: uci,
            adSrc: src
        };

        if (MESO_AD_EVENTS.PASS === code) {
            emitAdSucceeded({gaiaId, adUci: uci, adSrc: src, adPlacement: slot});
            newState.status = MESO_SLOT_STATUSES.LOADED;
            onAdLoadSuccess && onAdLoadSuccess();
        } else if (MESO_AD_EVENTS.FAIL === code || MESO_AD_EVENTS.EMPTY === code) {
            emitAdFailed({failedReason: code, gaiaId, adUci: uci, adSrc: src, adPlacement: slot})
            newState.status = MESO_SLOT_STATUSES.FAILED;
            onAdLoadFailed && onAdLoadFailed();
        }

        this.setState(newState);
    }

    get loadSuccess() {
        return this.state.status === MESO_SLOT_STATUSES.LOADED;
    }

    getContainerClasses() {
        const {slot, strategy} = this.props;
        const slotClass = `meso-ad-${slot}`;
        const classes = {
            [slotClass]: true
        };

        if (strategy === 'expand') {
            classes[slotClass] = this.loadSuccess;
            classes['meso-ad-loading'] = !this.loadSuccess;
        }

        return classNames('meso-ad-container', classes);
    }

    onError() {
        const {slot, gaiaId} = this.props;
        const {adUci, adSrc} = this.state;

        emitAdFailed({
            failedReason: MESO_SLOT_STATUSES.RENDERING_FAILURE,
            adPlacement: slot,
            gaiaId,
            adUci,
            adSrc
        });
    }

    onVisible() {
        const {slot, gaiaId} = this.props;
        const {adUci, adSrc} = this.state;

        emitAdView({
            adPlacement: slot,
            gaiaId,
            adUci,
            adSrc
        });
    }

    render() {
        const {slot, style = {}} = this.props;
        const slotClasses = this.getContainerClasses();

        return (
            <ErrorBoundary onError={this.onError}>
                <div className={slotClasses} style={style}>
                    <Placeholder extraClasses="meso-ad-placeholder"/>
                    <meso-display-ad ref={this.mesoDisplayAdRef} uci={slot} slot={slot}/>
                    {this.loadSuccess && <Observer onVisible={this.onVisibleOnce}>
                            {(ref) => <div ref={ref} />}
                        </Observer>}
                </div>
            </ErrorBoundary>
        );
    }
}

export const MesoAds = function ({slot, strategy, onAdLoadSuccess, onAdLoadFailed, onAdLoading, style}) {
    return (
        <Context.Consumer>
            {({allowAds, gaiaId}) => allowAds ?
                <MesoSlot
                    gaiaId={gaiaId}
                    slot={slot}
                    strategy={strategy}
                    onAdLoadSuccess={onAdLoadSuccess}
                    onAdLoadFailed={onAdLoadFailed}
                    onAdLoading={onAdLoading}
                    style={style}
                />
                : null
            }
        </Context.Consumer>
    );
};

MesoAds.defaultProps = {
    strategy: 'expand'
};

MesoAds.propTypes = {
    slot: PropTypes.string,
    strategy: PropTypes.oneOf([
        'expand', 'collapse'
    ]),
    onAdLoadSuccess: PropTypes.func,
    onAdLoadFailed: PropTypes.func,
    onAdLoading: PropTypes.func,
    style: PropTypes.object
};
